import { StorageServiceFactory } from "@/services/storage/StorageServiceFactory";
import { StoragePaths, type StoragePathSegments } from "@/utils/storagePaths";
import { getMimeType, getOptimalQuality, isWebPSupported } from "./webpSupport";

/**
 * Generate a thumbnail from an image file in the browser
 * Optimized for large images by using intermediate downsampling
 */
export const generateClientThumbnail = async (
  file: File,
  width: number = 1024,
  height: number = 512,
  format: "jpeg" | "webp" = "webp",
): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d", {
      willReadFrequently: false,
      alpha: false, // Disable alpha for better performance
    });

    if (!ctx) {
      reject(new Error("Could not get 2D context for canvas"));
      return;
    }

    const img = new Image();

    // Use decode API if available for better performance
    const imageLoadPromise = new Promise<void>((resolveLoad, rejectLoad) => {
      img.onload = () => resolveLoad();
      img.onerror = (error) => rejectLoad(new Error(`Failed to load image: ${error}`));
      img.src = URL.createObjectURL(file);
    });

    // Decode image if supported (improves performance)
    const decodePromise =
      "decode" in img ? img.decode().catch(() => imageLoadPromise) : imageLoadPromise;

    decodePromise
      .then(() => {
        // For very large images, use an intermediate canvas to downsample first
        // This significantly improves performance for images > 4K
        const MAX_INTERMEDIATE_SIZE = 4096;
        const isVeryLarge = img.width > MAX_INTERMEDIATE_SIZE || img.height > MAX_INTERMEDIATE_SIZE;

        let sourceWidth = img.width;
        let sourceHeight = img.height;
        let x = 0;
        let y = 0;

        // Determine if the image is wider or taller than the target aspect ratio
        const imageAspectRatio = sourceWidth / sourceHeight;
        const targetAspectRatio = width / height;

        if (imageAspectRatio > targetAspectRatio) {
          // Image is wider, crop horizontally
          sourceWidth = sourceHeight * targetAspectRatio;
          x = (img.width - sourceWidth) / 2;
        } else {
          // Image is taller, crop vertically
          sourceHeight = sourceWidth / targetAspectRatio;
          y = (img.height - sourceHeight) / 2;
        }

        // For very large images, use intermediate canvas for better performance
        if (isVeryLarge) {
          const intermediateCanvas = document.createElement("canvas");
          const intermediateCtx = intermediateCanvas.getContext("2d", { alpha: false });

          if (!intermediateCtx) {
            reject(new Error("Could not get 2D context for intermediate canvas"));
            return;
          }

          // Calculate intermediate size (maintain aspect ratio, max dimension = MAX_INTERMEDIATE_SIZE)
          const scale = Math.min(
            MAX_INTERMEDIATE_SIZE / sourceWidth,
            MAX_INTERMEDIATE_SIZE / sourceHeight,
          );
          const intermediateWidth = Math.floor(sourceWidth * scale);
          const intermediateHeight = Math.floor(sourceHeight * scale);

          intermediateCanvas.width = intermediateWidth;
          intermediateCanvas.height = intermediateHeight;

          // Draw to intermediate canvas first (much faster for large images)
          intermediateCtx.drawImage(
            img,
            x,
            y,
            sourceWidth,
            sourceHeight,
            0,
            0,
            intermediateWidth,
            intermediateHeight,
          );

          // Now draw from intermediate to final canvas
          canvas.width = width;
          canvas.height = height;
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = "high";
          ctx.drawImage(intermediateCanvas, 0, 0, width, height);
        } else {
          // For smaller images, draw directly
          canvas.width = width;
          canvas.height = height;
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = "high";
          ctx.drawImage(img, x, y, sourceWidth, sourceHeight, 0, 0, width, height);
        }

        // Determine the best format to use
        let mimeType: string;
        let quality: number;

        if (format === "webp" && isWebPSupported()) {
          mimeType = getMimeType("webp");
          quality = getOptimalQuality("webp");
        } else {
          mimeType = getMimeType("jpeg");
          quality = getOptimalQuality("jpeg");
        }

        // Convert canvas to blob
        canvas.toBlob(
          (blob) => {
            // Clean up object URL
            URL.revokeObjectURL(img.src);

            if (blob) {
              resolve(blob);
            } else {
              reject(new Error("Canvas toBlob failed"));
            }
          },
          mimeType,
          quality,
        );
      })
      .catch((error) => {
        reject(
          new Error(`Failed to process image for thumbnail generation: ${error.message || error}`),
        );
      });
  });
};

/**
 * Generate both JPEG and WebP thumbnails for a panorama
 */
export const generateClientThumbnails = async (
  file: File,
  width: number = 1024,
  height: number = 512,
): Promise<{ jpeg: Blob; webp: Blob | null }> => {
  try {
    // Always generate JPEG (fallback format)
    const jpegBlob = await generateClientThumbnail(file, width, height, "jpeg");

    // Generate WebP if supported
    let webpBlob: Blob | null = null;
    if (isWebPSupported()) {
      webpBlob = await generateClientThumbnail(file, width, height, "webp");
    }

    return { jpeg: jpegBlob, webp: webpBlob };
  } catch (error) {
    console.error("Error generating thumbnails:", error);
    // Fallback to JPEG only if WebP fails
    const jpegBlob = await generateClientThumbnail(file, width, height, "jpeg");
    return { jpeg: jpegBlob, webp: null };
  }
};

/**
 * Uploads a generated thumbnail blob to craftspace storage.
 */
export const uploadClientThumbnail = async (
  thumbnailBlob: Blob,
  craftspaceId: string,
  projectId: string,
  panoramaName: string,
  format: "jpeg" | "webp" = "jpeg",
  storageSegments?: StoragePathSegments,
): Promise<string> => {
  const extension = format === "webp" ? "webp" : "jpg";
  const timestamp = Date.now();
  const thumbnailPath = StoragePaths.private.thumbnail(
    storageSegments?.craftspaceKey ?? craftspaceId,
    storageSegments?.projectKey ?? projectId,
    panoramaName,
    `thumbnail_${timestamp}.${extension}`,
  );

  try {
    // Get the appropriate storage service for this craftspace
    const storageService = await StorageServiceFactory.createStorageService(craftspaceId);

    // Convert blob to file for upload
    const thumbnailFile = new File([thumbnailBlob], `thumbnail_${timestamp}.${extension}`, {
      type: format === "webp" ? "image/webp" : "image/jpeg",
    });

    // Upload using the storage service
    const uploadPath = await storageService.uploadFile(thumbnailFile, thumbnailPath);

    // Get the file URL
    const fileUrl = await storageService.getFileUrl(uploadPath);

    return fileUrl;
  } catch (error) {
    console.error("Error uploading thumbnail:", error);
    const message = error instanceof Error ? error.message : "Unknown error";
    throw new Error(`Failed to upload thumbnail: ${message}`);
  }
};

/**
 * Upload both JPEG and WebP thumbnails and return URLs
 */
export const uploadClientThumbnails = async (
  thumbnails: { jpeg: Blob; webp: Blob | null },
  craftspaceId: string,
  projectId: string,
  panoramaName: string,
  storageSegments?: StoragePathSegments,
): Promise<{ thumbnail_url: string; thumbnail_url_webp: string | null }> => {
  try {
    // Upload JPEG thumbnail
    const jpegUrl = await uploadClientThumbnail(
      thumbnails.jpeg,
      craftspaceId,
      projectId,
      panoramaName,
      "jpeg",
      storageSegments,
    );

    // Upload WebP thumbnail if available
    let webpUrl: string | null = null;
    if (thumbnails.webp) {
      webpUrl = await uploadClientThumbnail(
        thumbnails.webp,
        craftspaceId,
        projectId,
        panoramaName,
        "webp",
        storageSegments,
      );
    }

    return { thumbnail_url: jpegUrl, thumbnail_url_webp: webpUrl };
  } catch (error) {
    console.error("Error uploading thumbnails:", error);
    throw error;
  }
};
